import React, { useState } from "react";
import "./DeviationDayCard.css";
import { DeviationDayCardType } from "../../features/deviationApproval/DeviationApprovalTypes";
import { PjpPlanConfigType } from "../../features/deviationApproval/DeviationApprovalTypes";
import EventNoteIcon from "@mui/icons-material/EventNote";
import {
  TranslationEnum,
  usePortalTranslation,
} from "../../providers/TranslationProvider";
import { Button, TextField } from "@mui/material";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { convertDateToDDMMYYYY, submitDeviationApprovalData } from "../../features/deviationApproval/DeviationApprovalService";

interface GenericDeviationCardProps {
  PjpDayData: DeviationDayCardType;
  pjpPlanOption: PjpPlanConfigType;
  fetchDeviationData: () => Promise<void>;
  deviationApprovalConfig: Record<string, any> | undefined;
}

const GenericDeviationCard: React.FC<GenericDeviationCardProps> = ({
  PjpDayData,
  pjpPlanOption,
  fetchDeviationData,
  deviationApprovalConfig,
}) => {
  const { translate } = usePortalTranslation();

  const [managerComment, setManagerComment] = useState<string>("");
  const [error, setError] = useState<boolean>(false);

  const onCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setManagerComment(event.target.value);
    if (event.target.value.trim()) {
      setError(false);
    }
  };

  const handleApprove = async (action: "approve" | "reject") => {
    if (!managerComment.trim()) {
      setError(true);

      openPopup(
        "Alert",
        translate(TranslationEnum.deviation_approval, "Please fill in Manager Comments before submitting"),
        () => {}
      );
    } else {
      setError(false);

      const res = await submitDeviationApprovalData(
        PjpDayData,
        managerComment,
        pjpPlanOption,
        action
      );

      if (res === "") {
        openPopup(
          "Success",
          translate(TranslationEnum.deviation_approval, "Your data has been submitted"),
          () => {
            setManagerComment("");
            setError(false);
            fetchDeviationData();
          }
        );
      } else if (res === "failed") {
        openPopup(
          "Error",
          translate(TranslationEnum.deviation_approval, "Something went wrong. Please try again after some time!"),
          () => {}
        );
      }
    }
  };

  // Find the matching plan config for extra data points
  const getExtraDataPoints = () => {
    if (!deviationApprovalConfig?.plan_options_config) return [];

    const planConfig = deviationApprovalConfig.plan_options_config.find(
      (config: any) =>
        config.id?.toLowerCase() === PjpDayData.PJPPlan?.toLowerCase()
    );

    return planConfig?.extra_data_points || [];
  };

  // Map data point ID to actual field value
  const getDataPointValue = (dataPointId: string): string => {
    const previousBeatValue = PjpDayData.previousBeatName && PjpDayData.previousBeatCode
      ? `${PjpDayData.previousBeatName} (${PjpDayData.previousBeatCode})`
      : translate(TranslationEnum.deviation_approval, "N/A");
    
    const requestedBeatValue = PjpDayData.requestedBeatName && PjpDayData.requestedBeatCode
      ? `${PjpDayData.requestedBeatName} (${PjpDayData.requestedBeatCode})`
      : translate(TranslationEnum.deviation_approval, "N/A");

    const dataPointMap: Record<string, string> = {
      previousBeatWithCode: previousBeatValue,
      requestedBeatWithCode: requestedBeatValue,
      pjpDate: convertDateToDDMMYYYY(PjpDayData.pjpDate) || translate(TranslationEnum.deviation_approval, "N/A"),
    };

    return dataPointMap[dataPointId] || translate(TranslationEnum.deviation_approval, "N/A");
  };

  const extraDataPoints = getExtraDataPoints();

  return (
    <div
      className="dev-card"
      style={{
        borderLeftColor: PjpDayData.textColor,
        borderLeftWidth: "4px",
        borderLeftStyle: "solid",
      }}
    >
      {/* Calendar icon on left, PJP plan tag on right */}
      <div className="dev-date-status">
        <div className="date">
          <EventNoteIcon />
          {PjpDayData.pjpDate || translate(TranslationEnum.deviation_approval, "N/A")}
        </div>
        <div>
          <div
            className="label"
            style={{
              backgroundColor: `${PjpDayData.textColor}1A`,
              color: PjpDayData.textColor,
            }}
          >
            {translate(TranslationEnum.deviation_approval, PjpDayData.PJPPlan)}
          </div>
        </div>
      </div>

      {/* User name: user name (user loginid) */}
      <div className="details" style={{ marginTop: "0.5rem", marginBottom: 0 }}>
        <div>
          <strong>{translate(TranslationEnum.deviation_approval, "Name")}:</strong>{" "}
          {PjpDayData.userName || translate(TranslationEnum.deviation_approval, "N/A")} ({PjpDayData.loginId || translate(TranslationEnum.deviation_approval, "N/A")})
        </div>
      </div>

      {/* Extra data points for current PJP plan */}
      {extraDataPoints.length > 0 && (
        <div className="details" style={{ marginTop: "0.5rem", marginBottom: 0 }}>
          {extraDataPoints.map((dataPoint: any, index: number) => {
            const value = getDataPointValue(dataPoint.id);
            const label = dataPoint.label 
              ? translate(TranslationEnum.deviation_approval, dataPoint.label)
              : translate(TranslationEnum.deviation_approval, "N/A");
            return (
              <div key={index}>
                <strong>{label}:</strong> {value}
              </div>
            );
          })}
        </div>
      )}

      {/* Remarks */}
      {PjpDayData.statusRemarks && (
        <div className="details" style={{ marginTop: "0.5rem", marginBottom: 0 }}>
          <div>
            <strong>
              {translate(TranslationEnum.deviation_approval, "Remarks")}:
            </strong>{" "}
            {PjpDayData.statusRemarks}
          </div>
        </div>
      )}

      {/* Manager Comment */}
      <div className="deviation-manager-comment" style={{ marginTop: "0.5rem" }}>
        <TextField
          id="outlined-multiline-static"
          label={translate(TranslationEnum.deviation_approval, "Manager Comment")}
          multiline
          rows={4}
          value={managerComment}
          onChange={onCommentChange}
          error={error}
          required
          helperText={error ? translate(TranslationEnum.deviation_approval, "Manager Comment cannot be empty") : ""}
          fullWidth
        />
      </div>

      {/* Buttons */}
      <div className="deviation-button-container">
        <Button
          variant="contained"
          onClick={() => handleApprove("reject")}
          color="error"
        >
          {translate(TranslationEnum.deviation_approval, "Decline")}
        </Button>
        <Button
          variant="contained"
          onClick={() => handleApprove("approve")}
          style={{ backgroundColor: "#00C6B2" }}
        >
          {translate(TranslationEnum.deviation_approval, "Approve")}
        </Button>
      </div>
    </div>
  );
};

export default GenericDeviationCard;

